import { useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const KEY = 'gedready-progress';

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}

export function useProgress(user) {
  const [progress, setProgress] = useState(load);

  // Load progress from Supabase whenever user changes (fixes race condition on login)
  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_progress')
      .select('subject, topic, correct, total')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const merged = {};
        data.forEach(row => {
          merged[row.topic] = { correct: row.correct, total: row.total, subject: row.subject };
        });
        setProgress(merged);
        localStorage.setItem(KEY, JSON.stringify(merged));
      });
  }, [user]);

  async function recordAnswer(topic, isCorrect, subject) {
    setProgress(prev => {
      const t = prev[topic] || { correct: 0, total: 0 };
      const next = {
        ...prev,
        [topic]: { correct: t.correct + (isCorrect ? 1 : 0), total: t.total + 1, subject },
      };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });

    // Sync to Supabase
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const current = progress[topic] || { correct: 0, total: 0 };
    await supabase.from('user_progress').upsert({
      user_id: session.user.id,
      subject: subject || '',
      topic,
      correct: current.correct + (isCorrect ? 1 : 0),
      total: current.total + 1,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,subject,topic' });
  }

  function getTopicAccuracy(topic) {
    const t = progress[topic];
    if (!t || t.total === 0) return null;
    return Math.round((t.correct / t.total) * 100);
  }

  function getTotalQuestions() {
    return Object.values(progress).reduce((s, t) => s + t.total, 0);
  }

  function getReadinessScore() {
    const vals = Object.values(progress).filter(t => t.total > 0);
    if (vals.length === 0) return 0;
    const correct = vals.reduce((s, t) => s + t.correct, 0);
    const total = vals.reduce((s, t) => s + t.total, 0);
    return Math.round((correct / total) * 100);
  }

  return { progress, recordAnswer, getTopicAccuracy, getTotalQuestions, getReadinessScore };
}
